<div class="info-card p-3 full-width" fxLayout="column" fxLayoutAlign="start center" fxLayoutGap="20px">
  <div class="full-width" fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="10px">
    <mat-icon>description</mat-icon>
    <span class="subtitle-default">Booking Rates and Charges</span>
  </div>

  <div class="body-1-regular-2 full-width" fxLayout="column" fxLayoutAlign="start start" fxLayoutGap="10px">
    <span>Lease rates can be based on more than one space. Use the toggles below to choose which space(s) should be used to calculate the effective rates and charges for this lease. If the selected spaces have compatible pricing, the system can combine their rates; otherwise, you can add a custom rate.</span>
    <span><span class="body-1-default">Important:</span> This selection is only for rate calculation. It does not change which spaces are reserved — all originally selected spaces remain reserved.</span>
  </div>

  <div class="full-width">
    <mat-divider></mat-divider>
    <div class="space-calculation">
      <div fxLayout="row" fxLayoutAlign="space-between center">
        <h3>Default Pricing</h3>
        <div>
          <mat-slide-toggle [checked]="useDefaultPricing" color="primary"
                            (change)="toggleDefaultPricing()"></mat-slide-toggle>
        </div>
      </div>

      <div class="cost-details mb-2">
        <div class="cost-item">
          <span>Space size:</span>
          <span>{{ bookingConfig.subLeaseSize }} <span>m<sup>2</sup></span></span>
        </div>
      </div>

      @if (hasDefaultPricing) {
        <div class="cost-details">
          <div class="cost-item">
            <span>{{ periodTypeRate | titlecase }} Rate:</span>
            <span>R{{ utils.displayDecimal(periodRate, 2) }}
              @if (fixedRateType == SpacePricingRateCategory.PER_SQM) {
                <span>/ </span><span>m<sup>2</sup></span>
              }
              </span>
          </div>
          <div class="cost-item">
            <span>Occupied {{ periodTypeUnit | titlecase }}s:</span>
            <span>{{ utils.displayDecimal(leasePeriods, 1) }} {{ periodTypeUnit }}s</span>
          </div>
          <div class="cost-item">
            <span>Lease Duration:</span>
            <span>{{ leasePeriodDays }} days</span>
          </div>
          <div class="cost-item">
            <span>Total Estimated Amount:</span>
            <span>R{{ utils.displayDecimal(estimatedCost, 2) }} {{ percentageCost }}</span>
          </div>

          <div class="cost-item sub-total">
            <span>Total Estimated Amount:</span>
            <span>R{{ utils.displayDecimal(rentPerPeriod, 2) }}
              per {{ periodTypeUnit }} {{ percentageCost }}</span>
          </div>
        </div>
      } @else {
        <app-info-panel [type]="PanelType.WARN"
                        [message]="['This space does not have default pricing set for the time period chosen.']"></app-info-panel>
      }
    </div>

    <mat-divider></mat-divider>

    <!--CUSTOM OPTION-->
    <div class="space-calculation">
      <div fxLayout="row" fxLayoutAlign="space-between center">
        <h3>Custom rates</h3>
        <div>
          <mat-slide-toggle [checked]="!useDefaultPricing" color="primary"
                            (change)="toggleDefaultPricing()"></mat-slide-toggle>
        </div>
      </div>

      <form class="cost-details" [formGroup]="customRateForm">
        <div class="cost-item">
          <span>Effective space size:</span>
          <span>{{ effectiveTotalSpace }} <span>m<sup>2</sup></span></span>
        </div>
        <div class="cost-item">
          <span>Fixed Rate:</span>
          <div fxLayout="row" fxLayoutGap="15px">
            <mat-button-toggle-group class="custom-labeled-button-toggle" formControlName="fixedCategory">
              <mat-button-toggle value="PER_PERIOD">
                Per {{ perPricingPeriod[periodType!].period }}
              </mat-button-toggle>
              <mat-button-toggle value="PER_SQM">
                Per m² per {{ perPricingPeriod[periodType!].period }}
              </mat-button-toggle>
            </mat-button-toggle-group>

            <mat-form-field class="custom-text-field" appearance="outline" floatLabel="always">
              <input matInput type="number" step="0.01" formControlName="fixedRate">
              <span matPrefix class="ms-3">R</span>
              @if (customRateForm.get('fixedCategory')!.value == SpacePricingRateCategory.PER_SQM) {
                <div matSuffix class="me-2"><span>/ </span><span class="ms-2">m<sup>2</sup></span></div>
              }
            </mat-form-field>
          </div>
        </div>
        <div class="cost-item">
          <span>Percentage Turnover:</span>
          <div fxLayout="row" fxLayoutGap="15px">
            <mat-button-toggle-group class="custom-labeled-button-toggle" formControlName="variableCategory">
              <mat-button-toggle value="PERCENTAGE">% Turnover</mat-button-toggle>
            </mat-button-toggle-group>
            <mat-form-field class="custom-text-field" appearance="outline" floatLabel="always">
              <input matInput type="number" formControlName="variableRate" step="0.5">
              <span matSuffix class="me-2">%</span>
            </mat-form-field>
          </div>
        </div>
        <div class="cost-item">
          <span>{{ periodType | titlecase }} Rate:</span>
          <span>R{{ utils.displayDecimal(customRateForm.get('fixedRate')!.value, 2) }}
            @if (customRateForm.get('fixedCategory')!.value == SpacePricingRateCategory.PER_SQM) {
              <span>/ </span><span>m<sup>2</sup></span>
            }
              </span>
        </div>
        <div class="cost-item">
          <span>Occupied {{ periodTypeUnit | titlecase }}s:</span>
          <span>{{ utils.displayDecimal(leasePeriods, 1) }} {{ periodTypeUnit }}s</span>
        </div>
        <div class="cost-item">
          <span>Lease Duration:</span>
          <span>{{ leasePeriodDays }} days</span>
        </div>

        <div class="cost-item">
          <span>Total Estimated Amount:</span>
          <span>R{{ utils.displayDecimal(customEstimatedCost, 2) }} {{ customPercentageCost }}</span>
        </div>

        <div class="cost-item sub-total">
          <span>Rent per period:</span>
          <span>R{{ utils.displayDecimal(customRentPerPeriod, 2) }}
            per {{ periodTypeUnit }} {{ customPercentageCost }}</span>
        </div>
      </form>
    </div>

    <mat-divider></mat-divider>

    <div class="cost-calculation">
      <h3>Final Estimation</h3>
      @if (((hasDefaultPricing && useDefaultPricing) || !useDefaultPricing) && bookingEstimation) {
        <div class="cost-details">
          <div class="cost-item">
            <span>Effective Spaces:</span>
            <span>{{ bookingEstimation.effectiveSpaces }}</span>
          </div>
          <div class="cost-item">
            <span>Effective space size:</span>
            <span>{{ bookingEstimation.totalSpace }} <span>m<sup>2</sup></span></span>
          </div>
          <div class="cost-item">
            <span>{{ bookingEstimation.periodType | titlecase }} Rate:</span>
            <span>R{{ utils.displayDecimal(bookingEstimation.fixedRate, 2) }}
              @if (bookingEstimation.fixedCategoryType == SpacePricingRateCategory.PER_SQM) {
                <span>/ </span><span>m<sup>2</sup></span>
              }
              </span>
          </div>
          <div class="cost-item">
            <span>Occupied {{ bookingEstimation.periodTypeUnit | titlecase }}s:</span>
            <span>{{ utils.displayDecimal(bookingEstimation.leasePeriods, 1) }} {{ bookingEstimation.periodTypeUnit }}
              s</span>
          </div>
          <div class="cost-item">
            <span>Lease Duration:</span>
            <span>{{ bookingEstimation.leasePeriodDays }} days</span>
          </div>
          <mat-divider></mat-divider>
          <div class="cost-item total">
            <span>Rent per period:</span>
            <span>R{{ utils.displayDecimal(bookingEstimation.rentPerPeriod, 2) }}
              per {{ bookingEstimation.periodTypeUnit }} {{ bookingEstimation.variableCost }}</span>
          </div>
          <div class="cost-item">
            <span><strong>Total Estimated Amount:</strong></span>
            <span><strong>R{{ utils.displayDecimal(bookingEstimation.fixedCost, 2) }} {{ bookingEstimation.variableCost }}</strong></span>
          </div>

        </div>
       } @else {
        <app-info-panel [type]="PanelType.WARN" [message]="['Cannot determine rates for selected spaces(s).']"
                        [buttons]="true"
                        [buttonLabels]="['Use Custom']"
                        (emitEvent)="addCustomRate()">>
        </app-info-panel>
      }

    </div>

    <div class="navigation-buttons">
      <button class="button-secondary" mat-flat-button (click)="prevStep()">Back</button>
      <button class="button-primary" mat-flat-button (click)="nextStep()" [disabled]="!canSubmit">
        <div class="button-text">{{ !retailerId ? 'Choose a tenant' : 'Review and Save' }}</div>
      </button>
    </div>
  </div>
</div>
